import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Spinner, Badge, Card, Carousel } from 'react-bootstrap';
import { FaBed, FaBath, FaCar, FaRulerCombined, FaMapMarkerAlt, FaCalendarAlt, FaHome, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { handleTokenAndPostRequest, formatCurrency, setMetaTags } from '../utils/utils';
import { useAgentDetails } from '../utils/useAgentDetails';
import './Styles.css';

const StatCard = ({ label, value, changeText, delta }) => {
  const color = changeText === '0%' ? '#888' : delta > 0 ? '#16a34a' : '#dc2626';
  return (
    <div style={{
      textAlign: 'center',
      padding: '20px 12px',
      backgroundColor: '#f8f9fa',
      borderRadius: 12,
      flex: 1,
      minWidth: 0,
    }}>
      <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: 6, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a1a' }}>{value ?? 'N/A'}</div>
      {changeText && (
        <div style={{ fontSize: '0.8rem', fontWeight: 600, color, marginTop: 4 }}>
          {delta > 0 ? <FaArrowUp size={10} style={{ marginRight: 3 }} /> : delta < 0 ? <FaArrowDown size={10} style={{ marginRight: 3 }} /> : null}
          {changeText}
        </div>
      )}
    </div>
  );
};

const PublicListing = () => {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [suburbData, setSuburbData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { agentDetails, branding } = useAgentDetails();

  // Agent colors with fallbacks
  const accentColor = branding?.accentColor || '#6366f1';
  const bgColor = agentDetails?.agent_colour || '#4338ca';

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch listing data
  useEffect(() => {
    const fetchListing = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/get-listing`,
          { id },
          { 'Content-Type': 'application/json' }
        );
        if (response.data && response.data.listing) {
          const listingData = response.data.listing;
          // Parse features if it's a JSON string
          if (typeof listingData.features === 'string') {
            try {
              listingData.features = JSON.parse(listingData.features);
            } catch (e) {
              listingData.features = [];
            }
          }
          // Parse media if it's a JSON string
          if (typeof listingData.media === 'string') {
            try {
              listingData.media = JSON.parse(listingData.media);
            } catch (e) {
              listingData.media = [];
            }
          }
          // Parse land_area if it's a JSON string
          if (typeof listingData.land_area === 'string') {
            try {
              listingData.land_area = JSON.parse(listingData.land_area);
            } catch (e) {
              // leave as-is if not valid JSON
            }
          }
          setListing(listingData);
        } else {
          setError('Listing not found.');
        }
      } catch (err) {
        console.error('Error fetching listing:', err);
        setError('Unable to load listing.');
      }
      setLoading(false);
    };
    fetchListing();
  }, [id]);

  // Fetch suburb data once listing is available
  useEffect(() => {
    if (!listing || !listing.suburb_id) return;
    const fetchSuburb = async () => {
      try {
        const response = await handleTokenAndPostRequest(
          `${window.api_host}/suburb`,
          {
            suburb_ids: [listing.suburb_id],
            generate_llm: false,
          },
          { 'Content-Type': 'application/json' }
        );
        if (response.data && response.data.length > 0) {
          setSuburbData(response.data[0]);
        }
      } catch (err) {
        console.error('Error fetching suburb data:', err);
      }
    };
    fetchSuburb();
  }, [listing]);

  // Meta tags
  useEffect(() => {
    if (!listing) return;
    setMetaTags({
      title: `${listing.display_address} | Property Listing`,
      description: listing.summary_description || `${listing.property_type_display} in ${listing.suburb}, ${listing.state} ${listing.postcode}`,
      keywords: `${listing.suburb}, ${listing.state}, ${listing.postcode}, ${listing.property_type_display}, real estate`,
      url: `https://homeprice.site/listing/${id}`,
      image: listing.photo_url_main || '',
    });
  }, [listing, id]);

  if (loading) {
    return (
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !listing) {
    return (
      <Container style={{ maxWidth: 800, paddingTop: 80, textAlign: 'center' }}>
        <h3 style={{ color: '#666' }}>{error || 'Listing not found.'}</h3>
      </Container>
    );
  }

  // Format sold date
  const soldDate = listing.sold_timestamp
    ? new Date(listing.sold_timestamp).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const listedDate = listing.date_listed
    ? new Date(listing.date_listed).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  // Suburb stat helpers
  const suburbMedianPrice = suburbData?.median_sold_price_filled
    ? `$${suburbData.median_sold_price_filled.toLocaleString('en-US')}`
    : 'N/A';

  const suburbMedianRent = suburbData?.median_rent_price_filled
    ? `$${suburbData.median_rent_price_filled.toLocaleString('en-US')}`
    : 'N/A';

  const soldPriceDelta =
    typeof suburbData?.median_sold_price_filled === 'number' &&
    typeof suburbData?.prior_median_sold_price === 'number' &&
    suburbData.prior_median_sold_price !== 0
      ? (((suburbData.median_sold_price_filled - suburbData.prior_median_sold_price) / suburbData.prior_median_sold_price) * 100).toFixed(0)
      : null;

  const rentPriceDelta =
    typeof suburbData?.median_rent_price_filled === 'number' &&
    typeof suburbData?.prior_median_rent_price === 'number' &&
    suburbData.prior_median_rent_price !== 0
      ? (((suburbData.median_rent_price_filled - suburbData.prior_median_rent_price) / suburbData.prior_median_rent_price) * 100).toFixed(0)
      : null;

  const soldDomDelta =
    typeof suburbData?.median_sold_days_on_market_filled === 'number' &&
    typeof suburbData?.prior_median_sold_days_on_market === 'number' &&
    suburbData.prior_median_sold_days_on_market !== 0
      ? (((suburbData.median_sold_days_on_market_filled - suburbData.prior_median_sold_days_on_market) / suburbData.prior_median_sold_days_on_market) * 100).toFixed(0)
      : null;

  // Status badge color
  const statusColor = listing.listing_status === 'Sold' ? '#16a34a' : listing.listing_status === 'Rent' ? '#2563eb' : accentColor;

  // Gather images for carousel
  const images = Array.isArray(listing.media)
    ? listing.media.filter(m => m.mediaCategory === 'IMAGE').map(m => m.url)
    : [];
  if (listing.photo_url_main && !images.includes(listing.photo_url_main)) {
    images.unshift(listing.photo_url_main);
  }

  return (
    <div style={{ backgroundColor: '#fafafa', minHeight: '100vh', paddingBottom: 60, paddingTop: isMobile ? 0 : 20 }}>
      {/* Image Carousel */}
      {images.length > 0 && (
        <div style={{
          maxWidth: 840,
          margin: '0 auto',
          position: 'relative',
          backgroundColor: '#e5e7eb',
          borderRadius: isMobile ? 0 : 16,
          overflow: 'hidden',
        }}>
          <Carousel
            indicators={images.length > 1}
            controls={images.length > 1}
            interval={null}
          >
            {images.map((url, idx) => (
              <Carousel.Item key={idx}>
                <img
                  src={url}
                  alt={`${listing.display_address} - ${idx + 1}`}
                  style={{
                    width: '100%',
                    height: isMobile ? 260 : 440,
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>
      )}

      {/* Price Display */}
      {listing.display_price && (
        <Container style={{ maxWidth: 840, paddingTop: 16 }}>
          <h1 style={{ fontSize: isMobile ? '1.5rem' : '2rem', fontWeight: 700, margin: 0, color: '#111' }}>
            {listing.display_price}
          </h1>
        </Container>
      )}

      <Container style={{ maxWidth: 840, paddingTop: 28 }}>
        {/* Address & Status */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
            <Badge
              bg="none"
              style={{
                backgroundColor: statusColor,
                fontSize: '0.75rem',
                fontWeight: 600,
                padding: '5px 12px',
                borderRadius: 20,
                color: '#fff',
                letterSpacing: '0.02em',
              }}
            >
              {listing.listing_status}
            </Badge>
            <Badge
              bg="none"
              style={{
                backgroundColor: '#f3f4f6',
                color: '#374151',
                fontSize: '0.75rem',
                fontWeight: 600,
                padding: '5px 12px',
                borderRadius: 20,
              }}
            >
              <FaHome size={11} style={{ marginRight: 4, marginTop: -2 }} />
              {listing.property_type_display}
            </Badge>
          </div>
          <h2 style={{ fontWeight: 700, fontSize: isMobile ? '1.3rem' : '1.6rem', color: '#111', marginBottom: 4 }}>
            {listing.display_address}
          </h2>
          <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>
            <FaMapMarkerAlt size={12} style={{ marginRight: 4 }} />
            {listing.suburb}, {listing.state} {listing.postcode}
          </div>
        </div>

        {/* Key Stats Row */}
        <div style={{
          display: 'flex',
          gap: isMobile ? 16 : 32,
          flexWrap: 'wrap',
          marginBottom: 28,
          padding: '20px 24px',
          backgroundColor: '#fff',
          borderRadius: 14,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>
          {listing.bedrooms != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaBed size={18} color={accentColor} />
              <span style={{ fontWeight: 600, fontSize: '1.05rem' }}>{listing.bedrooms}</span>
              <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Beds</span>
            </div>
          )}
          {listing.bathrooms != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaBath size={18} color={accentColor} />
              <span style={{ fontWeight: 600, fontSize: '1.05rem' }}>{listing.bathrooms}</span>
              <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Baths</span>
            </div>
          )}
          {listing.carspaces != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaCar size={18} color={accentColor} />
              <span style={{ fontWeight: 600, fontSize: '1.05rem' }}>{listing.carspaces}</span>
              <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Cars</span>
            </div>
          )}
          {listing.land_area != null && (typeof listing.land_area === 'object' ? listing.land_area.size > 0 : listing.land_area > 0) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaRulerCombined size={16} color={accentColor} />
              <span style={{ fontWeight: 600, fontSize: '1.05rem' }}>{typeof listing.land_area === 'object' ? listing.land_area.size : listing.land_area}</span>
              <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>{typeof listing.land_area === 'object' ? listing.land_area.unit : 'm²'}</span>
            </div>
          )}
        </div>

        {/* Timeline Info */}
        <Row style={{ marginBottom: 28 }}>
          {listedDate && (
            <Col xs={12} md={soldDate ? 4 : 6} style={{ marginBottom: isMobile ? 12 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#4b5563' }}>
                <FaCalendarAlt size={14} color="#9ca3af" />
                <span style={{ fontSize: '0.88rem' }}>Listed {listedDate}</span>
              </div>
            </Col>
          )}
          {soldDate && (
            <Col xs={12} md={4} style={{ marginBottom: isMobile ? 12 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#4b5563' }}>
                <FaCalendarAlt size={14} color="#16a34a" />
                <span style={{ fontSize: '0.88rem' }}>Sold {soldDate}</span>
              </div>
            </Col>
          )}
          {listing.days_on_market != null && (
            <Col xs={12} md={4}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#4b5563' }}>
                <FaCalendarAlt size={14} color="#9ca3af" />
                <span style={{ fontSize: '0.88rem' }}>{listing.days_on_market} days on market</span>
              </div>
            </Col>
          )}
        </Row>

        {/* Price Details Card */}
        {(listing.price || listing.price_lower || listing.price_upper) && (
          <Card style={{ border: 'none', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 28 }}>
            <Card.Body style={{ padding: '24px 28px' }}>
              <h5 style={{ fontWeight: 700, fontSize: '1.05rem', color: '#111', marginBottom: 16 }}>Price Details</h5>
              <div style={{ display: 'flex', gap: isMobile ? 20 : 48, flexWrap: 'wrap' }}>
                {listing.price != null && (
                  <div>
                    <div style={{ color: '#9ca3af', fontSize: '0.8rem', fontWeight: 500, marginBottom: 4 }}>Price</div>
                    <div style={{ fontWeight: 700, fontSize: '1.2rem', color: '#111' }}>${listing.price.toLocaleString('en-US')}</div>
                  </div>
                )}
                {listing.price_lower != null && (
                  <div>
                    <div style={{ color: '#9ca3af', fontSize: '0.8rem', fontWeight: 500, marginBottom: 4 }}>Estimated Low</div>
                    <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#4b5563' }}>${listing.price_lower.toLocaleString('en-US')}</div>
                  </div>
                )}
                {listing.price_upper != null && (
                  <div>
                    <div style={{ color: '#9ca3af', fontSize: '0.8rem', fontWeight: 500, marginBottom: 4 }}>Estimated High</div>
                    <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#4b5563' }}>${listing.price_upper.toLocaleString('en-US')}</div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Description */}
        {listing.summary_description && (
          <Card style={{ border: 'none', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 28 }}>
            <Card.Body style={{ padding: '24px 28px' }}>
              <h5 style={{ fontWeight: 700, fontSize: '1.05rem', color: '#111', marginBottom: 12 }}>About this property</h5>
              <p style={{ color: '#374151', lineHeight: 1.7, fontSize: '0.95rem', marginBottom: 0, whiteSpace: 'pre-line' }}>
                {listing.summary_description}
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Features */}
        {listing.features && listing.features.length > 0 && (
          <Card style={{ border: 'none', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 28 }}>
            <Card.Body style={{ padding: '24px 28px' }}>
              <h5 style={{ fontWeight: 700, fontSize: '1.05rem', color: '#111', marginBottom: 16 }}>Features</h5>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {listing.features.map((feature, idx) => (
                  <Badge
                    key={idx}
                    bg="none"
                    style={{
                      backgroundColor: `${accentColor}15`,
                      color: accentColor,
                      fontSize: '0.82rem',
                      padding: '6px 14px',
                      borderRadius: 20,
                      fontWeight: 500,
                    }}
                  >
                    {feature.replace(/([A-Z])/g, ' $1').trim()}
                  </Badge>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Suburb Snapshot */}
        {suburbData && (
          <Card style={{ border: 'none', borderRadius: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginBottom: 28 }}>
            <Card.Body style={{ padding: '28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
                <h5 style={{ fontWeight: 700, fontSize: '1.05rem', color: '#111', margin: 0 }}>
                  {suburbData.suburb_initcap} Suburb Snapshot
                </h5>
                {suburbData.reporting_month && (
                  <span style={{ fontSize: '0.78rem', color: '#9ca3af', fontWeight: 500 }}>
                    As of {suburbData.reporting_month}
                  </span>
                )}
              </div>

              {/* Suburb Summary */}
              {suburbData.suburb_summary && (
                <p style={{ color: '#4b5563', fontSize: '0.9rem', lineHeight: 1.65, marginBottom: 24 }}>
                  {suburbData.suburb_summary}
                </p>
              )}

              {/* Sale Market */}
              <div style={{ marginBottom: 20 }}>
                <h6 style={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem', marginBottom: 12 }}>Sale Market</h6>
                <div style={{ display: 'flex', gap: 10, flexDirection: isMobile ? 'column' : 'row' }}>
                  <StatCard
                    label="Median Sold Price"
                    value={suburbMedianPrice}
                    changeText={suburbData.median_sold_price_percent_change_text}
                    delta={soldPriceDelta}
                  />
                  <StatCard
                    label="Median Days on Market"
                    value={suburbData.median_sold_days_on_market_filled}
                    changeText={suburbData.median_sold_days_on_market_percent_change_text}
                    delta={soldDomDelta}
                  />
                  <StatCard
                    label="Sold Properties"
                    value={suburbData.sold_properties}
                    changeText={suburbData.sold_properties_percent_change_text}
                    delta={
                      typeof suburbData.sold_properties === 'number' &&
                      typeof suburbData.prior_sold_properties === 'number' &&
                      suburbData.prior_sold_properties !== 0
                        ? (((suburbData.sold_properties - suburbData.prior_sold_properties) / suburbData.prior_sold_properties) * 100).toFixed(0)
                        : null
                    }
                  />
                </div>
              </div>

              {/* Rental Market */}
              <div>
                <h6 style={{ fontWeight: 600, color: '#374151', fontSize: '0.9rem', marginBottom: 12 }}>Rental Market</h6>
                <div style={{ display: 'flex', gap: 10, flexDirection: isMobile ? 'column' : 'row' }}>
                  <StatCard
                    label="Median Rent Price"
                    value={suburbMedianRent}
                    changeText={suburbData.median_rent_price_percent_change_text}
                    delta={rentPriceDelta}
                  />
                  <StatCard
                    label="Median Days on Market"
                    value={suburbData.median_rent_days_on_market_filled}
                    changeText={suburbData.median_rent_days_on_market_percent_change_text}
                    delta={
                      typeof suburbData.median_rent_days_on_market_filled === 'number' &&
                      typeof suburbData.prior_median_rent_days_on_market === 'number' &&
                      suburbData.prior_median_rent_days_on_market !== 0
                        ? (((suburbData.median_rent_days_on_market_filled - suburbData.prior_median_rent_days_on_market) / suburbData.prior_median_rent_days_on_market) * 100).toFixed(0)
                        : null
                    }
                  />
                  <StatCard
                    label="Rented Properties"
                    value={suburbData.rent_properties}
                    changeText={suburbData.rent_properties_percent_change_text}
                    delta={
                      typeof suburbData.rent_properties === 'number' &&
                      typeof suburbData.prior_rent_properties === 'number' &&
                      suburbData.prior_rent_properties !== 0
                        ? (((suburbData.rent_properties - suburbData.prior_rent_properties) / suburbData.prior_rent_properties) * 100).toFixed(0)
                        : null
                    }
                  />
                </div>
              </div>

              {/* Link to suburb profile */}
              <div style={{ marginTop: 20, textAlign: 'center' }}>
                <Link
                  to={`/suburb-profile/${listing.suburb_id}`}
                  style={{
                    color: accentColor,
                    fontWeight: 600,
                    fontSize: '0.88rem',
                    textDecoration: 'none',
                  }}
                >
                  View full {suburbData.suburb_initcap} suburb profile →
                </Link>
              </div>
            </Card.Body>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default PublicListing;
